import { defineStore } from "pinia";
import { ref, computed } from "vue";
import ApiCompanies from "@/apis/ApiCompanies";
import ApiCompanyAccesses from "@/apis/ApiCompanyAccesses";
import { catchable } from "./piniaUtils";
import { useToastsStore } from "./toastsStore";

export const useCompaniesStore = defineStore("companies", () => {
  // Clean up old localStorage entries from previous implementation
  localStorage.removeItem("viewing_company_id");

  const companies = ref([]);
  const accessibleCompanies = ref([]);
  const viewingCompanySlug = ref(localStorage.getItem("viewing_company_slug") || "");
  const viewingCompanyName = ref(localStorage.getItem("viewing_company_name") || "");
  const viewingCompanyRole = ref(localStorage.getItem("viewing_company_role") || "");

  const createCompany = ({ companyName, awsMarketplaceCustomerId, awsMarketplaceEnabled }) =>
    catchable({
      t: async () => {
        const company = await ApiCompanies.createCompany(
          companyName,
          awsMarketplaceCustomerId,
          awsMarketplaceEnabled,
        );
        companies.value = [...companies.value, company];
      },
      throwable: true,
    });

  const getCompanies = loadable =>
    catchable({
      t: async () => (companies.value = await ApiCompanies.getCompanies()),
      loadable,
    });

  const getAccessibleCompanies = (userId, loadable) =>
    catchable({
      t: async () => {
        const response = await ApiCompanyAccesses.getAccessibleCompanies(userId);
        // API returns an object with accessible_companies array, extract just the array
        accessibleCompanies.value = response?.accessible_companies || [];
      },
      loadable,
    });

  const setViewingCompany = ({ slug, name, role, auto = false }) => {
    const previousSlug = viewingCompanySlug.value;

    viewingCompanySlug.value = slug || "";
    viewingCompanyName.value = name || "";
    viewingCompanyRole.value = role || "";

    if (slug) {
      localStorage.setItem("viewing_company_slug", slug || "");
      localStorage.setItem("viewing_company_name", name || "");
      localStorage.setItem("viewing_company_role", role || "");
    } else {
      localStorage.removeItem("viewing_company_slug");
      localStorage.removeItem("viewing_company_name");
      localStorage.removeItem("viewing_company_role");
    }

    // Show toast notification only once when auto-switching to a different company
    if (auto && previousSlug !== slug) {
      const toastsStore = useToastsStore();
      toastsStore.create({
        type: "info",
        title: "Company Context Switched",
        body: `Now viewing ${name}`,
        timeout: 5000,
        bodyTransform: null,
      });
    }
  };

  const clearViewingCompany = () => {
    viewingCompanySlug.value = "";
    viewingCompanyName.value = "";
    viewingCompanyRole.value = "";
    localStorage.removeItem("viewing_company_slug");
    localStorage.removeItem("viewing_company_name");
    localStorage.removeItem("viewing_company_role");
  };

  return {
    companies,
    accessibleCompanies,
    viewingCompanySlug,
    viewingCompanyName,
    viewingCompanyRole,
    getCompanies,
    getAccessibleCompanies,
    createCompany,
    setViewingCompany,
    clearViewingCompany,
  };
});
